import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../../api';
import { t } from '../../i18n';
import { icon } from '../ui/icons';
import './lang-menu';

/**
 * <cadence-auth-shell heading="…" sub="…"> — the framed sign-in card shared by the
 * admin and portal login screens. It renders the school brand (logo + name) in a
 * colored band topped by a "cadence" beat-bar motif, then projects the page's form
 * via the default slot. Branding comes from the public GET /auth/brand.
 */
@customElement('cadence-auth-shell')
export class CadenceAuthShell extends LitElement {
  @property() heading = '';
  @property() sub = '';
  @state() private brand: { name: string; logo_url: string | null; tagline?: string | null } | null = null;

  // Heights (of 20) for the beat bars — an irregular rhythm reads as a beat, not a chart.
  private static readonly BEAT = [9, 15, 7, 19, 12, 17, 10, 14];

  connectedCallback(): void {
    super.connectedCallback();
    api.get<{ name: string; logo_url: string | null; tagline?: string | null }>('/auth/brand')
      .then((b) => (this.brand = b))
      .catch(() => { /* fall back to the app name */ });
  }

  static styles = css`
    :host {
      display: grid; place-items: center; min-height: 100vh; padding: var(--space-4);
      /* Soft glow of the theme primary instead of flat grey. */
      background:
        radial-gradient(1100px 520px at 50% -8%, color-mix(in srgb, var(--color-primary) 18%, var(--color-bg-secondary)), var(--color-bg-secondary));
    }
    .card {
      position: relative;
      width: 100%; max-width: 420px; overflow: hidden;
      background: var(--color-bg-primary); border: 1px solid var(--color-border);
      border-radius: var(--radius-lg); box-shadow: var(--shadow-lg);
    }
    /* Sits in the top-right of the brand band, over the gradient. */
    .lang { position: absolute; top: var(--space-4); right: var(--space-4); z-index: 5; }

    /* Brand band ---------------------------------------------------------- */
    .band {
      padding: var(--space-5) var(--space-5) var(--space-4);
      background: linear-gradient(135deg, var(--color-primary), var(--color-secondary, var(--color-primary)));
      color: #fff;
    }
    .identity { display: flex; align-items: center; gap: 14px; padding-right: 52px; }
    .tile {
      width: 46px; height: 46px; flex-shrink: 0; border-radius: var(--radius-md);
      display: grid; place-items: center; background: rgba(255, 255, 255, 0.9);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
    .tile img { width: 100%; height: 100%; object-fit: contain; border-radius: inherit; }
    .tile svg { width: 24px; height: 24px; color: var(--color-primary); }
    .id-text { display: flex; flex-direction: column; min-width: 0; }
    .school {
      font-size: 1.4rem; font-weight: 700; letter-spacing: -0.01em; line-height: 1.15;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .kicker { font-size: 0.74rem; letter-spacing: 0.08em; text-transform: uppercase; opacity: 0.82; }

    /* Beat-bar motif (the signature) ------------------------------------- */
    .beat { display: flex; align-items: flex-end; gap: 5px; height: 20px; margin-top: 18px; }
    .beat span {
      width: 5px; border-radius: 3px; background: rgba(255, 255, 255, 0.65);
      transform-origin: bottom; animation: rise 0.5s cubic-bezier(0.22, 1, 0.36, 1) backwards;
    }
    @keyframes rise { from { transform: scaleY(0.15); opacity: 0.4; } to { transform: scaleY(1); opacity: 1; } }
    @media (prefers-reduced-motion: reduce) { .beat span { animation: none; } }

    /* Form body ----------------------------------------------------------- */
    .body { padding: var(--space-6); }
    h1 { margin: 0 0 4px; font-size: 1.35rem; font-weight: 700; letter-spacing: -0.01em; }
    p.sub { margin: 0 0 var(--space-5); color: var(--color-text-secondary); font-size: 0.92rem; }

    @media (max-width: 460px) { .body { padding: var(--space-5); } }
  `;

  render() {
    const name = this.brand?.name || t('common.app.name');
    return html`
      <div class="card">
        <cadence-lang-menu dark class="lang"></cadence-lang-menu>
        <div class="band">
          <div class="identity">
            <span class="tile">
              ${this.brand?.logo_url
                ? html`<img src=${this.brand.logo_url} alt="" />`
                : icon('music', 24)}
            </span>
            <span class="id-text">
              <span class="school">${name}</span>
              ${this.brand?.tagline ? html`<span class="kicker">${this.brand.tagline}</span>` : ''}
            </span>
          </div>
          <div class="beat" aria-hidden="true">
            ${CadenceAuthShell.BEAT.map((h, i) => html`<span style=${`height:${h}px;animation-delay:${0.15 + i * 0.05}s`}></span>`)}
          </div>
        </div>
        <div class="body">
          <h1>${this.heading}</h1>
          ${this.sub ? html`<p class="sub">${this.sub}</p>` : ''}
          <slot></slot>
        </div>
      </div>
    `;
  }
}
